import {
  TestBuy,
  TestBuyProductWithCriteria,
} from "@coworker/types/lib/testbuy";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import React from "react";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { useGetRandomItems } from "@coworker/apprestructured/src/testbuy/hooks/useGetRandomItems";
import { TYPES_OF_ARTICLES } from "@coworker/app/src/components/TestBuy/constants";
import { Trans, useTranslation } from "@coworker/locales";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { TestbuyItemSearch } from "@coworker/apprestructured/src/testbuy/components/TestbuyItemSearch/TestbuyItemSearch";
import { ItemBadge } from "@coworker/apprestructured/src/shared/types/ItemBadge";
import { getArticleCompletionStatus } from "@coworker/app/src/components/TestBuy/CommonComponents/article.helpers";
import { ScannerErrorComponent } from "@ingka-group-digital/flexa-scanner";
import { computeBadgeText } from "@coworker/apprestructured/src/testbuy/helpers/computeBadgeText";
import { useReplaceItem } from "@coworker/apprestructured/src/testbuy/hooks/useReplaceItem";
import { SwipeableTestbuyListViewItem } from "@coworker/apprestructured/src/testbuy/components/SwipeableTestbuyListViewItem/SwipeableTestbuyListViewItem";
import { TestbuyListViewItem } from "@coworker/apprestructured/src/testbuy/components/TestbuyListViewItem/TestbuyListViewItem";
import { useGetTestbuyFollowUpTask } from "@coworker/apprestructured/src/testbuy/hooks/useGetTestbuyFollowUpTask";
import {
  trackSwapRandomTestBuyArticle,
  trackSwapScannedTestBuyArticle,
} from "@coworker/app/src/helpers/tracker";
import { ExtractedArticleInformationType } from "@coworker/apprestructured/src/scan-and-search/types/ExtractedArticleInformationType";

interface TestbuyItemsListViewItemProps {
  testbuy: TestBuy;
  testbuyItem: TestBuyProductWithCriteria;
  setReplaceArticleLoading: (replaceArticleLoading: boolean) => void;
  replaceArticleLoading: boolean;
  allTestbuyItemNos?: string[];
  isCompletedTestbuy?: boolean;
  isItemCompletedInTestbuy?: boolean;
}

export const TestbuyItemsListViewItem = ({
  testbuy,
  testbuyItem,
  setReplaceArticleLoading,
  replaceArticleLoading,
  allTestbuyItemNos,
  isCompletedTestbuy,
  isItemCompletedInTestbuy,
}: TestbuyItemsListViewItemProps) => {
  const isChina = isChineseEnvironment();
  const { showToast } = useToastNotification();
  const [error, setError] = React.useState<string | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const { t } = useTranslation();

  const { data: testbuyFollowupTask } = useGetTestbuyFollowUpTask(
    testbuyItem?.followUpTaskId || ""
  );

  const { data: itemInfo, isLoading: isLoadingItemInfo } = useItemInfo(
    testbuyItem.shortId
  );

  const { refetch } = useGetRandomItems(
    testbuy?.area ?? "",
    (testbuy?.typeOfArticles ?? TYPES_OF_ARTICLES[1]?.type) as string,
    allTestbuyItemNos
  );

  const replaceItemMutation = useReplaceItem(testbuy.id, testbuyItem.shortId);

  const handleGetNewItem = async (currentItemShortId: string) => {
    trackSwapRandomTestBuyArticle(
      testbuy.typeOfArticles,
      currentItemShortId,
      testbuy.area
    );
    setReplaceArticleLoading(true);
    try {
      const result = await refetch();
      const fetchedItems = result.data;

      if (fetchedItems && fetchedItems.length > 0) {
        const randomArticleReplacement = fetchedItems[0]?.shortId;
        if (randomArticleReplacement) {
          await replaceItemMutation.mutateAsync(randomArticleReplacement);
        } else {
          showToast(
            <>
              <Trans>taskErrorMessage</Trans>
              <br />
              <Trans>tryAgainString</Trans>
            </>,
            undefined,
            undefined,
            5
          );
        }
      } else {
        showToast(<Trans>taskErrorMessage</Trans>, undefined, undefined, 5);
      }
    } catch {
      showToast(<Trans>taskErrorMessage</Trans>, undefined, undefined, 5);
    } finally {
      setReplaceArticleLoading(false);
    }
  };

  const badgeText = computeBadgeText(testbuyFollowupTask, t);
  const badges: ItemBadge[] = badgeText
    ? [{ text: badgeText, colour: "green" }]
    : [];

  const navigationUrl = `/testbuy/${testbuy.id}/article/${testbuyItem.id}`;

  const handleChangeWithClick = (currentItemShortId: string) => {
    trackSwapScannedTestBuyArticle(
      testbuy.typeOfArticles,
      currentItemShortId,
      testbuy.area
    );
    setIsSearching(true);
  };

  const handleScanResult = async (
    itemInfo: ExtractedArticleInformationType
  ) => {
    const itemNo = itemInfo.articleNumber;
    if (allTestbuyItemNos?.includes(itemNo)) {
      setError("ARTICLE_ALREADY_EXISTS");
    } else {
      await replaceItemMutation.mutateAsync(itemNo);
    }
    setIsSearching(false);
  };

  const articleStatus = getArticleCompletionStatus(testbuyItem);
  const swipingEnabled =
    (!isChina || !isCompletedTestbuy || !isItemCompletedInTestbuy) &&
    articleStatus === "NotStarted";

  if (
    replaceItemMutation.isPending ||
    isLoadingItemInfo ||
    replaceArticleLoading
  )
    return <SimpleArticleCardSkeleton />;

  if (replaceItemMutation.isError) {
    showToast(
      <Trans>couldNotUseThisArticleString</Trans>,
      undefined,
      undefined,
      5
    );
  }

  return (
    <>
      <TestbuyItemSearch
        setIsSearching={setIsSearching}
        isSearching={isSearching}
        handleScanResult={handleScanResult}
      />
      <ScannerErrorComponent error={error} setError={setError} />
      {!isLoadingItemInfo && !replaceItemMutation.isPending && itemInfo && (
        <>
          {swipingEnabled ? (
            <SwipeableTestbuyListViewItem
              navigationUrl={navigationUrl}
              testbuyItem={testbuyItem}
              item={itemInfo as Item}
              onChangeWithClick={() =>
                handleChangeWithClick(testbuyItem.shortId)
              }
              onGetNewItemClick={() => handleGetNewItem(testbuyItem.shortId)}
            />
          ) : (
            <TestbuyListViewItem
              navigationUrl={navigationUrl}
              badges={
                isCompletedTestbuy ? badges.filter((badge) => badge.text) : []
              }
              item={itemInfo}
              testbuyItem={testbuyItem}
            />
          )}
        </>
      )}
    </>
  );
};
